/**
 * Editor inline de salud del proyecto. Muestra el badge actual; al hacer
 * click se expande con select + textarea para health_note. Mutation PATCH.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Check, X } from "lucide-react";
import clsx from "clsx";
import { api } from "@/lib/api";

const HEALTH_OPTIONS = [
  { value: "green", label: "Saludable", badge: "badge-success" },
  { value: "yellow", label: "Atención", badge: "badge-warning" },
  { value: "red", label: "Riesgo", badge: "badge-error" },
  { value: "unknown", label: "Sin definir", badge: "badge-ghost" },
] as const;

type Health = (typeof HEALTH_OPTIONS)[number]["value"];

export function HealthEditor({
  projectId,
  health,
  healthNote,
}: {
  projectId: string;
  health: string;
  healthNote: string | null | undefined;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draftHealth, setDraftHealth] = useState<Health>(health as Health);
  const [draftNote, setDraftNote] = useState(healthNote ?? "");

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/projects/${projectId}`, {
        health: draftHealth,
        healthNote: draftNote.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      setEditing(false);
    },
  });

  const cancel = () => {
    setDraftHealth(health as Health);
    setDraftNote(healthNote ?? "");
    setEditing(false);
  };

  const current = HEALTH_OPTIONS.find((o) => o.value === health) ?? HEALTH_OPTIONS[3];

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="group w-full flex items-start justify-between gap-3 -m-1 p-1 rounded-md hover:bg-base-200/60 transition-colors text-left"
      >
        <div className="space-y-1.5">
          <span className={clsx("badge badge-sm", current.badge)}>{current.label}</span>
          {healthNote ? (
            <p className="text-sm text-base-content/70 leading-relaxed">{healthNote}</p>
          ) : (
            <p className="text-xs text-base-content/40 italic">Click para anotar contexto.</p>
          )}
        </div>
        <Pencil className="size-3.5 text-base-content/30 group-hover:text-primary transition-colors mt-1 shrink-0" />
      </button>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {HEALTH_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setDraftHealth(opt.value)}
            className={clsx(
              "badge badge-sm cursor-pointer transition-all",
              opt.badge,
              draftHealth === opt.value
                ? "ring-2 ring-offset-1 ring-offset-base-100 ring-current"
                : "opacity-50 hover:opacity-100",
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <textarea
        rows={3}
        className="textarea textarea-bordered w-full text-sm"
        placeholder="Nota: por qué está así, qué falta para mejorarlo, etc."
        value={draftNote}
        onChange={(e) => setDraftNote(e.target.value)}
      />
      <div className="flex justify-end gap-1.5">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={cancel}
          disabled={save.isPending}
        >
          <X className="size-3.5" /> Cancelar
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => save.mutate()}
          disabled={save.isPending}
        >
          {save.isPending ? (
            <span className="loading loading-spinner loading-xs" />
          ) : (
            <Check className="size-3.5" />
          )}{" "}
          Guardar
        </button>
      </div>
    </div>
  );
}
